import React, { useEffect, useState } from 'react';
import { Activity, AlertTriangle, CheckCircle, TrendingUp, TrendingDown, RefreshCw } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface ProjectHealthScoreProps {
  projectId: string;
}

interface HealthScore {
  overall_score: number;
  documentation_score: number;
  activity_score: number;
  progress_score: number;
  risk_level: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  risk_factors: Array<{
    type: string;
    severity: string;
    message: string;
  }>;
  recommendations: Array<{
    priority: string;
    action: string;
    category: string;
  }>;
  calculated_at?: string;
}

export const ProjectHealthScore: React.FC<ProjectHealthScoreProps> = ({ projectId }) => {
  const [healthScore, setHealthScore] = useState<HealthScore | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCalculating, setIsCalculating] = useState(false);

  useEffect(() => {
    loadHealthScore();
  }, [projectId]);

  const loadHealthScore = async () => {
    try {
      setIsLoading(true);

      // Try to get latest score
      const { data: existing, error: fetchError } = await supabase
        .from('project_health_scores')
        .select('*')
        .eq('project_id', projectId)
        .order('calculated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (fetchError) throw fetchError;

      if (existing) {
        setHealthScore(existing as any);
      } else {
        // Calculate if no score exists
        await calculateHealth();
      }
    } catch (error) {
      console.error('Error loading health score:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateHealth = async () => {
    try {
      setIsCalculating(true);

      // Call the calculate_project_health function
      const { data, error } = await supabase
        .rpc('calculate_project_health', { p_project_id: projectId });

      if (error) throw error;

      // Save the result
      const { data: saved, error: saveError } = await supabase
        .from('project_health_scores')
        .insert({
          project_id: projectId,
          overall_score: data.overall_score,
          documentation_score: data.documentation_score,
          activity_score: data.activity_score,
          progress_score: data.progress_score,
          risk_level: data.risk_level,
          risk_factors: data.risk_factors,
          recommendations: data.recommendations,
          calculated_by: (await supabase.auth.getUser()).data.user?.id
        })
        .select()
        .maybeSingle();

      if (saveError) throw saveError;

      setHealthScore(saved as any);
    } catch (error) {
      console.error('Error calculating health:', error);
    } finally {
      setIsCalculating(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreBackground = (score: number) => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-blue-100';
    if (score >= 40) return 'bg-orange-100';
    return 'bg-red-100';
  };

  const getRiskBadgeColor = (risk: string) => {
    const colors = {
      LOW: 'bg-green-100 text-green-800',
      MEDIUM: 'bg-yellow-100 text-yellow-800',
      HIGH: 'bg-orange-100 text-orange-800',
      CRITICAL: 'bg-red-100 text-red-800'
    };
    return colors[risk as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getRiskLabel = (risk: string) => {
    const labels = {
      LOW: 'Faible',
      MEDIUM: 'Moyen',
      HIGH: 'Élevé',
      CRITICAL: 'Critique'
    };
    return labels[risk as keyof typeof labels] || risk;
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!healthScore) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="text-center py-8">
          <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Score de santé non disponible</h3>
          <p className="text-gray-600 mb-4">Calculez le score de santé pour ce projet</p>
          <button
            onClick={calculateHealth}
            disabled={isCalculating}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {isCalculating ? 'Calcul...' : 'Calculer'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Diagnostic du projet</h3>
        <button
          onClick={calculateHealth}
          disabled={isCalculating}
          className="flex items-center space-x-2 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isCalculating ? 'animate-spin' : ''}`} />
          <span>Actualiser</span>
        </button>
      </div>

      {/* Overall Score */}
      <div className="flex items-center justify-center">
        <div className="relative w-32 h-32">
          <svg className="w-full h-full transform -rotate-90">
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke="currentColor"
              strokeWidth="8"
              fill="none"
              className="text-gray-200"
            />
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke="currentColor"
              strokeWidth="8"
              fill="none"
              strokeDasharray={`${(healthScore.overall_score / 100) * 2 * Math.PI * 56} ${2 * Math.PI * 56}`}
              className={getScoreColor(healthScore.overall_score)}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`text-3xl font-bold ${getScoreColor(healthScore.overall_score)}`}>
              {healthScore.overall_score}
            </span>
            <span className="text-xs text-gray-500">/ 100</span>
          </div>
        </div>
      </div>

      {/* Risk Level */}
      <div className="flex items-center justify-center">
        <span className={`px-4 py-1.5 rounded-full text-sm font-medium ${getRiskBadgeColor(healthScore.risk_level)}`}>
          Risque {getRiskLabel(healthScore.risk_level)}
        </span>
      </div>

      {/* Score Breakdown */}
      <div className="space-y-3">
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-gray-600">Documentation</span>
            <span className={`text-sm font-medium ${getScoreColor(healthScore.documentation_score)}`}>
              {healthScore.documentation_score}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${getScoreBackground(healthScore.documentation_score)}`}
              style={{ width: `${healthScore.documentation_score}%` }}
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-gray-600">Activité</span>
            <span className={`text-sm font-medium ${getScoreColor(healthScore.activity_score)}`}>
              {healthScore.activity_score}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${getScoreBackground(healthScore.activity_score)}`}
              style={{ width: `${healthScore.activity_score}%` }}
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-gray-600">Progression</span>
            <span className={`text-sm font-medium ${getScoreColor(healthScore.progress_score)}`}>
              {healthScore.progress_score}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${getScoreBackground(healthScore.progress_score)}`}
              style={{ width: `${healthScore.progress_score}%` }}
            />
          </div>
        </div>
      </div>

      {/* Risk Factors */}
      {healthScore.risk_factors && healthScore.risk_factors.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center space-x-2">
            <AlertTriangle className="w-4 h-4 text-orange-600" />
            <span>Facteurs de risque</span>
          </h4>
          <div className="space-y-2">
            {healthScore.risk_factors.map((factor, index) => (
              <div key={index} className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-sm text-orange-900">{factor.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {healthScore.recommendations && healthScore.recommendations.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center space-x-2">
            <CheckCircle className="w-4 h-4 text-blue-600" />
            <span>Recommandations</span>
          </h4>
          <div className="space-y-2">
            {healthScore.recommendations.map((rec, index) => (
              <div key={index} className="flex items-start space-x-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <TrendingUp className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-900">{rec.action}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {healthScore.calculated_at && (
        <p className="text-xs text-gray-500 text-center">
          Dernière mise à jour : {new Date(healthScore.calculated_at).toLocaleString('fr-FR')}
        </p>
      )}
    </div>
  );
};
